import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  findTopmostShapeAtCanvasPoint,
  isCanvasPointInShapeBounds,
} from '../helpers/click-away-hit-test'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/** After a click-away exit: select the topmost shape under the click, or clear the selection. */
function selectClickAwayTarget(
  mgr: ShapeManagerRef | null,
  pt: { canvasX: number; canvasY: number } | null
): void {
  if (mgr && pt) {
    const shapes = mgr.getShapes()
    const hit = findTopmostShapeAtCanvasPoint(shapes, pt)

    if (hit) {
      mgr.selectShapes([hit.id])
    } else {
      mgr.clearSelection()
    }
  }
}

/**
 * Click-away for the node-edit and crop modes: a pasteboard click outside the edited shape exits
 * the mode and selects whatever was clicked (or nothing).
 */
export function useEditModeClickAway({
  internalShapeManagerRef,
  getCanvasPointFromPointerEvent,
  isNodeEditMode,
  isPenEditMode,
  nodeEditShapeData,
  nodeEditShapeIdRef,
  setNodeEditShapeId,
  setNodeEditShapeData,
  onNodeEditModeChange,
  isCropMode,
  cropShapeData,
  setCropShapeId,
  setCropShapeData,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  isNodeEditMode: boolean
  isPenEditMode: boolean
  nodeEditShapeData: ShapeData | null
  nodeEditShapeIdRef: MutableRefObject<string | null>
  setNodeEditShapeId: Dispatch<SetStateAction<string | null>>
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  onNodeEditModeChange: CenterCanvasConfig['onNodeEditModeChange']
  isCropMode: boolean
  cropShapeData: ShapeData | null
  setCropShapeId: Dispatch<SetStateAction<string | null>>
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
}) {
  const handleEditModeClickAwayPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      // Node edit mode: clicks inside the edited shape's bounds stay in node edit.
      // Clicks outside exit node edit and optionally select another shape.
      if (isNodeEditMode && e.button === 0) {
        // In pen-edit sub-mode, the NodeEditOverlay's overlay rect handles all clicks.
        if (isPenEditMode) return true

        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

        if (pt && nodeEditShapeData) {
          const insideBounds = isCanvasPointInShapeBounds(pt, nodeEditShapeData)

          if (insideBounds) return true
        }

        e.preventDefault()
        const exitingShapeId = nodeEditShapeData?.id ?? nodeEditShapeIdRef.current

        setNodeEditShapeId(null)
        setNodeEditShapeData(null)
        onNodeEditModeChange?.(false, exitingShapeId ?? undefined)
        selectClickAwayTarget(internalShapeManagerRef.current, pt)

        return true
      }

      // Crop mode: clicks inside the frame are handled by CropOverlay's own interior
      // pan-drag rect and never reach here — a click that does reach here is always outside
      // the frame. Exit crop mode and optionally select whatever was clicked, mirroring
      // node-edit mode's click-away behavior above.
      if (isCropMode && e.button === 0) {
        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

        if (pt && cropShapeData) {
          const insideBounds = isCanvasPointInShapeBounds(pt, cropShapeData)

          if (insideBounds) return true
        }

        e.preventDefault()
        setCropShapeId(null)
        setCropShapeData(null)
        selectClickAwayTarget(internalShapeManagerRef.current, pt)

        return true
      }

      return false
    },
    [
      getCanvasPointFromPointerEvent,
      isNodeEditMode,
      isPenEditMode,
      nodeEditShapeData,
      onNodeEditModeChange,
      isCropMode,
      cropShapeData,
      nodeEditShapeIdRef,
      setCropShapeData,
      setCropShapeId,
      setNodeEditShapeData,
      setNodeEditShapeId,
      internalShapeManagerRef,
    ]
  )

  return { handleEditModeClickAwayPointerDown }
}
